import NiceModal from '@ebay/nice-modal-react'
import clsx from 'clsx'
import React from 'react'
import DesignSystemVerificationModal from './DesignSystemVerificationModal'
import { VerificationSubject } from './subjects'
import { useVerifications, VerificationRow, VerificationState } from './VerificationContext'

const PRESENTATION: Record<VerificationState, { icon: string; className: string; title: string }> =
    {
        approved: {
            icon: 'check_circle',
            className: 'text-green-600 hover:text-green-700',
            title: 'Approved',
        },
        stale: {
            icon: 'update',
            className: 'text-amber-600 hover:text-amber-700',
            title: 'Approved, but the design changed since',
        },
        changes_requested: {
            icon: 'error',
            className: 'text-red-600 hover:text-red-700',
            title: 'Changes requested',
        },
        pending: {
            icon: 'pause_circle',
            className: 'text-amber-600 hover:text-amber-700',
            title: 'Pending',
        },
        unreviewed: {
            icon: 'radio_button_unchecked',
            className: 'text-text-quaternary hover:text-text-tertiary',
            title: 'Not verified yet',
        },
    }

interface Props {
    subject: VerificationSubject
    className?: string
}

/**
 * The per-subject sign-off indicator. Placement is the caller's job via
 * `className` so the same badge works in a token row and in front of a
 * component showcase title.
 */
export const VerificationBadge: React.FC<Props> = ({ subject, className }) => {
    const { getState, getRow, applySaved, canWrite } = useVerifications()
    const state = getState(subject)
    const row = getRow(subject)
    const { icon, className: stateClassName, title } = PRESENTATION[state]

    const onClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        // The showcase Card expands on any header click; keep that from
        // swallowing the badge.
        event.stopPropagation()

        const saved: VerificationRow | null = await NiceModal.show(DesignSystemVerificationModal, {
            subject,
            currentRow: row,
            canWrite,
            onSaved: applySaved,
        })

        if (saved) {
            applySaved(saved)
        }
    }

    return (
        <button
            type="button"
            onClick={onClick}
            title={row?.reviewed_by_name ? `${title} by ${row.reviewed_by_name}` : title}
            aria-label={`Verification status of ${subject.label}: ${title}`}
            className={clsx(
                'inline-flex shrink-0 leading-[1] transition-colors',
                stateClassName,
                className,
            )}>
            {/* Inlined rather than using the shared MIcon: shared.tsx renders this
                badge, and importing back into it would be a cycle. */}
            <i className="material-symbols-outlined text-display-sm leading-[1]" aria-hidden="true">
                {icon}
            </i>
        </button>
    )
}
